import React, { useState, useEffect, useRef } from "react";
import ApiService from "../../services/Api.service";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Alert from "../../components/Alert/Alert";
import Pagination from "../../components/Pagination/Pagination";
import StatusBadge from "../../utilities/StatusBadge";
import FilterBar from "../../components/FilterBar/FilterBar";
import TableSubSection from "../../components/leads/TableSubSection";
import SkeletonLoader from "../../components/SkeletonLoader/SkeletonLoader";
import WebsocketService from "../../services/Websocket.service";
import TokenService from "../../services/Token.service";
import DispositionSubmitForm from "./DispositionSubmitForm";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";

export default function TransferCalls() {
    const [activeTab, setActiveTab] = useState("Live Transfers");
    const tabs = ["Live Transfers", "Transfer History"];
    const [Loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [sections, setSections] = useState([]);
    const [postsPerPage, setPostsPerPage] = useState(20);
    const [headers, setHeaders] = useState([]);
    const [dropDownData, setDropDownData] = useState([]);
    const socketRef = useRef(null);
    const [wsConnected, setWsConnected] = useState(false);

    const [activeCallId, setActiveCallId] = useState(null);
    const [lastFilters, setLastFilters] = useState({});

    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        total_pages: 0,
    });
    const [alertValue, setAlertValue] = useState({
        show: false,
        message: "",
        variant: "success",
        position: "middle",
    });
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [dispositionOptions, setDispositionOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [selectedDisposition, setSelectedDisposition] = useState({});
    const [selectedSubDisposition, setSelectedSubDisposition] = useState({});
    const [callbackDates, setCallbackDates] = useState({});
    const [remarks, setRemarks] = useState({});
    const [submittingDisposition, setSubmittingDisposition] = useState({});
    const [claimedCalls, setClaimedCalls] = useState({});
    const [openDispositionCallId, setOpenDispositionCallId] = useState(null);

    const validationSchema = yup.object().shape({});

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            campaign_id: "",
            agents: [],
            products: [],
            search: "",
            start_date: "",
            end_date: "",
        },
    });

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [campaignRes, dispositionRes, statusRes] = await Promise.all([
                ApiService.getCampaignOptions(),
                ApiService.getDispositionOptions(),
                ApiService.getCallStatusOptions()
            ]);
            setCampaignOptions(campaignRes?.data?.data || []);
            setDispositionOptions(dispositionRes?.data?.data || []);
            setStatusOptions(statusRes?.data?.data || []);
        } catch (err) {
            console.error("Error loading dashboard data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchHistoryCalls = async (page = 1, size = postsPerPage, payload = lastFilters) => {
        try {
            setLoading(true);
            const queryPayload = { ...(payload || {}), transfer: true };
            setLastFilters(payload || {});
            const res = await ApiService.getAllCallList(queryPayload, { page, size });
            if (res.status === 200 && res.data) {
                setTableData(res.data.data || []);
                setPagination(res.data.pagination || { page, size, total: 0, total_pages: 0 });
                const receivedHeaders = res.data.header || [];
                setHeaders(receivedHeaders.filter((h) => h.value !== "action" && h.value !== "audit_status"));
                setSections(res.data.sections || []);
                setSelectedRowIndex(null);
            } else {
                setTableData([]);
                setHeaders([]);
                setSections([]);
            }
        } catch (error) {
            console.error("fetchHistoryCalls error:", error);
        } finally {
            setLoading(false);
            setActiveCallId(null);
        }
    };

    useEffect(() => {
        setSelectedRowIndex(null);
        setOpenDispositionCallId(null);
        if (activeTab === "Transfer History") {
            fetchHistoryCalls(1, postsPerPage);
        } else {
            setTableData([]);
            setPagination({ page: 1, size: 20, total: 0, total_pages: 0 });
        }
    }, [activeTab]);

    const sendWsEvent = (event, data) => {
        if (socketRef.current?.readyState !== WebSocket.OPEN) return false;
        socketRef.current.send(JSON.stringify({ event, data }));
        return true;
    };

    const getCallId = (item) => item?.call_id || item?.id || item?.callId;

    const isDisconnectedCall = (item) => {
        const status = String(item?.status || "").toLowerCase();
        const transferStatus = String(item?.transfer_status || "").toLowerCase();
        const hasNoStatusFlag = !status || status === "--" || status === "null" || status === "undefined";
        return (
            hasNoStatusFlag ||
            ["disconnected", "ended", "completed", "hangup", "failed"].includes(status) ||
            ["disconnected", "ended", "completed", "hangup", "failed"].includes(transferStatus)
        );
    };

    const handleDispositionChange = (callId, value) => {
        setSelectedDisposition((prev) => ({ ...prev, [callId]: value }));
        setSelectedSubDisposition((prev) => ({ ...prev, [callId]: "" }));
        setCallbackDates((prev) => ({ ...prev, [callId]: "" }));
    };

    const handleSubDispositionChange = (callId, value) => {
        setSelectedSubDisposition((prev) => ({ ...prev, [callId]: value }));
        const normalized = String(value || "").toLowerCase().replace(/\s+/g, " ").trim();
        if (normalized !== "call back") {
            setCallbackDates((prev) => ({ ...prev, [callId]: "" }));
        }
    };

    const handleCallbackDateChange = (callId, value) => {
        setCallbackDates((prev) => ({ ...prev, [callId]: value }));
    };

    const handleRemarkChange = (callId, value) => {
        setRemarks((prev) => ({ ...prev, [callId]: value }));
    };

    const handleSubmitDisposition = async (callId) => {
        const row = tableData.find((item) => getCallId(item) === callId);
        const disposition = selectedDisposition[callId];
        const subDisposition = selectedSubDisposition[callId] || "";
        const callbackDate = callbackDates[callId] || "";
        const remark = remarks[callId] || "";
        if (!row) return;

        if (!disposition) {
            setAlertValue({
                show: true,
                message: "Please select disposition",
                variant: "danger",
                position: "middle",
            });
            return;
        }

        try {
            setSubmittingDisposition((prev) => ({ ...prev, [callId]: true }));
            const payload = {
                campaign_id: row?.campaign_id || row?.campaign || "",
                disposition: disposition,
                sub_disposition: subDisposition,
                callback_date: callbackDate || null,
                remark: remark || "",
                call_id: callId
            };
            await ApiService.updateCallEndDisposition(payload);

            setTableData((prev) =>
                prev.map((item) =>
                    getCallId(item) === callId
                        ? {
                            ...item,
                            disposition,
                            sub_disposition: subDisposition,
                            callback_date: callbackDate || null,
                            remark: remark || "",
                            status: "ended"
                          }
                        : item
                )
            );

            setAlertValue({
                show: true,
                message: "Disposition submitted successfully",
                variant: "success",
                position: "middle",
            });
            setOpenDispositionCallId(null);
        } catch (error) {
            setAlertValue({
                show: true,
                message: error?.response?.data?.detail || "Failed to submit disposition",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setSubmittingDisposition((prev) => ({ ...prev, [callId]: false }));
        }
    };
    useEffect(() => {
        const ws = new WebSocket(WebsocketService.getTransferCalls());
        socketRef.current = ws;

        ws.onopen = () => {
            setWsConnected(true);
            const token = TokenService.getLocalAccessToken();
            const data = {
                access_token: token
            };
            sendWsEvent("connect", data);
        };

        ws.onmessage = (messageEvent) => {
            let payload;
            try {
                payload = JSON.parse(messageEvent.data);
            } catch (error) {
                console.error("Invalid websocket payload:", messageEvent.data);
                return;
            }

            const event = payload?.event;
            const data = payload?.data;

            switch (event) {
                case "headers": {
                    if (Array.isArray(data) && data.length) {
                        setHeaders((prev) => (activeTab === "Live Transfers" ? data : prev));
                    }
                    break;
                }
                case "transfer": {
                    if (data && activeTab === "Live Transfers") {
                        setTableData((prev) => [data, ...prev]);
                        setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
                    }
                    break;
                }
                case "claimed": {
                    const callId = data?.call_id;
                    if (callId && activeTab === "Live Transfers") {
                        setTableData((prev) => prev.filter((item) => getCallId(item) !== callId));
                        setPagination((prev) => ({
                            ...prev,
                            total: Math.max(prev.total - 1, 0),
                        }));
                    }
                    break;
                }
                default:
                    break;
            }
        };

        ws.onerror = (err) => {
            console.error("Live transfer ws error:", err);
            setWsConnected(false);
        };

        ws.onclose = () => {
            setWsConnected(false);
        };

        return () => ws.close();
    }, [activeTab]);

    const fetchDropdownDetail = async () => {
        try {
            const subprocessRes = await ApiService.getSubprocesses();
            if (subprocessRes.status === 200) setDropDownData(subprocessRes.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchDropdownDetail();
    }, []);

    const handelSubmit = async (data) => {
        const payload = Object.fromEntries(
            Object.entries(data).filter(([_, v]) => {
                if (Array.isArray(v)) return v.length > 0;
                return v !== "" && v !== null && v !== undefined;
            })
        );
        if (activeTab === "Transfer History") {
            fetchHistoryCalls(1, postsPerPage, payload);
        }
    };

    const handleClearFilters = () => {
        reset({ campaign_id: "", agents: [], products: [], search: "", start_date: "", end_date: "" });
        if (activeTab === "Transfer History") {
            fetchHistoryCalls(1, postsPerPage, {});
        }
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPostsPerPage(newSize);
        if (activeTab === "Transfer History") {
            fetchHistoryCalls(1, newSize);
        }
        setSelectedRowIndex(null);
    };

    const filterDefs = activeTab === "Transfer History" ? [
         { type: "select", name: "campaign_id", placeholder: "Campaign", options: campaignOptions },
        { type: "multiselect", name: "disposition", placeholder: "Disposition - All", options: dispositionOptions },
        { type: "multiselect", name: "status", placeholder: "Status - All", options: statusOptions },
        { type: "date", name: "start_date", placeholder: "Start Date" },
        { type: "date", name: "end_date", placeholder: "End Date" },
    ] : [];

    const mistableTh = {
        backgroundColor: "transparent",
        color: "rgba(43, 58, 103, 1)",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "0.1em",
        textAlign: "center",
    };

    const tableTdStyles = {
        backgroundColor: "#F7F7F7",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: "700",
        lineHeight: "16px",
        letterSpacing: "0em",
        textAlign: "center",
        color: "#232122",
    };

    const isHistory = activeTab === "Transfer History";

    return (
        <>
            {alertValue.show && (
                <Alert
                    variant={alertValue.variant}
                    message={alertValue.message}
                    dismissible={true}
                    timeout={2500}
                    position={alertValue.position}
                    onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
                />
            )}

            <div className="main-contanier">
                <div className="bulk-download-call-tabs">
                    {tabs.map((tab) => (
                        <div
                            key={tab}
                            className={`tab ${activeTab === tab ? "active" : ""}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </div>
                    ))}
                </div>

                <FilterBar
                    control={control}
                    handleSubmit={handleSubmit}
                    onSubmit={handelSubmit}
                    onClearFilters={handleClearFilters}
                    filters={filterDefs}
                    title={isHistory ? "Transfer Calls History" : "Live Transfer Calls"}
                    totalCount={pagination.total || 0}
                    tableId="table-id"
                    isDownload={false}
                />

                <div
                    className="table-container"
                    style={{
                        height: isHistory ? "calc(100vh - 260px)" : undefined,
                        position: "relative",
                        width: "100%",
                        overflowX: "auto"
                    }}
                >
                    {Loading ? (
                        <SkeletonLoader type="table" rows={8} cols={5} />
                    ) : (
                        <table id="table-id" className="custom-table-container">
                            <thead
                                className="table-head"
                                style={{ position: "sticky", top: "0", zIndex: "1" }}
                            >
                                <tr>
                                    <th style={mistableTh}>S.No</th>
                                    {headers.map((header, i) => (
                                        <th key={i} style={mistableTh}>
                                            {header.label}
                                        </th>
                                    ))}
                                    {isHistory ? (
                                        <th style={mistableTh}>Recording</th>
                                    ) : (
                                        <th style={mistableTh}>Actions</th>
                                    )}
                                </tr>
                            </thead>

                            <tbody>
                                {tableData.length > 0 ? (
                                    tableData.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <tr
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    setSelectedRowIndex(selectedRowIndex === index ? null : index)
                                                }
                                            >
                                                <td
                                                    style={{
                                                        ...tableTdStyles,
                                                        backgroundColor:
                                                            selectedRowIndex === index ? "#f9faff" : "transparent",
                                                    }}
                                                >
                                                    {index + 1}
                                                </td>
                                                {headers.map((header, i) => (
                                                    <td
                                                        key={i}
                                                        style={{
                                                            ...tableTdStyles,
                                                            backgroundColor:
                                                                selectedRowIndex === index ? "#f9faff" : "transparent",
                                                        }}
                                                        title={item[header.value] ?? "--"}
                                                    >
                                                        {header.value === "status" ? (
                                                            <StatusBadge status={item.status} />
                                                        ) : header.value === "disposition" ? (
                                                            <StatusBadge status={item.disposition} />
                                                        ) : (
                                                            <span className={header.value === "summary" || header.value === "call_summary" ? "cell-paragraph-ellipsis" : "cell-ellipsis"}>
                                                                {item[header.value] ?? "--"}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}

                                                {/* Actions / Audio Column for History vs Live */}
                                                {isHistory ? (
                                                    <td
                                                        style={{
                                                            ...tableTdStyles,
                                                            backgroundColor:
                                                                selectedRowIndex === index ? "#f9faff" : "transparent",
                                                        }}
                                                    >
                                                        <AudioPlayer
                                                            callId={item.id}
                                                            duration={item.duration}
                                                            activeCallId={activeCallId}
                                                            setActiveCallId={setActiveCallId}
                                                        />
                                                    </td>
                                                ) : (
                                                    <td
                                                        style={{
                                                            ...tableTdStyles,
                                                            backgroundColor:
                                                                selectedRowIndex === index ? "#f9faff" : "transparent",
                                                        }}
                                                    >
                                                        {claimedCalls[item.call_id] ? (
                                                            <span style={{ color: "green", fontWeight: "bold" }}>Claimed</span>
                                                        ) : (
                                                            <button
                                                                className="popup-add-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const callId = getCallId(item);
                                                                    sendWsEvent("claim", {
                                                                        call_id: callId,
                                                                        agent_id: TokenService.getLocalAccessToken(),
                                                                        campaign_id: item.campaign_id,
                                                                        company_id: item.company_id
                                                                    });
                                                                    setClaimedCalls((prev) => ({ ...prev, [callId]: true }));
                                                                }}
                                                                disabled={wsConnected ? false : true}
                                                                style={{ padding: "5px 10px", fontSize: "12px" }}
                                                            >
                                                                Claim
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>

                                            {/* Expanded Sub Section */}
                                            {selectedRowIndex === index && (
                                                <tr>
                                                    <td
                                                        colSpan={headers.length + 2}
                                                        style={{
                                                            padding: "12px",
                                                            maxWidth: 0,
                                                            backgroundColor: "#fcfcfd",
                                                        }}
                                                    >
                                                        <TableSubSection
                                                            data={item}
                                                            sections={sections}
                                                            onOpenCloseDisposition={(callId) => setOpenDispositionCallId(callId)}
                                                            onBackDisposition={() => setOpenDispositionCallId(null)}
                                                            isDispositionOpen={openDispositionCallId === getCallId(item)}
                                                            dispositionForm={
                                                                !isHistory && openDispositionCallId === getCallId(item) ? (
                                                                    <DispositionSubmitForm
                                                                        callId={getCallId(item)}
                                                                        campaignId={item?.campaign_id || item?.campaign || ""}
                                                                        isDisconnected={isDisconnectedCall(item)}
                                                                        options={dispositionOptions}
                                                                        value={selectedDisposition[getCallId(item)] || ""}
                                                                        subDisposition={selectedSubDisposition[getCallId(item)] || ""}
                                                                        callbackDate={callbackDates[getCallId(item)] || ""}
                                                                        remark={remarks[getCallId(item)] || ""}
                                                                        submitting={Boolean(submittingDisposition[getCallId(item)])}
                                                                        onChange={handleDispositionChange}
                                                                        onSubDispositionChange={handleSubDispositionChange}
                                                                        onCallbackDateChange={handleCallbackDateChange}
                                                                        onRemarkChange={handleRemarkChange}
                                                                        onSubmit={handleSubmitDisposition}
                                                                    />
                                                                ) : null
                                                            }
                                                            isCallEndButton={!isHistory && Boolean(claimedCalls[getCallId(item)])}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={headers.length + 2} style={tableTdStyles}>
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {isHistory && (
                    <Pagination
                        pagination={pagination}
                        postsPerPage={postsPerPage}
                        handlePageSizeChange={handlePageSizeChange}
                        fetchDetail={fetchHistoryCalls}
                    />
                )}
            </div>
        </>
    );
}
